import React, { useEffect, useMemo, useState } from 'react';
import './SecondStep.scss';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import useAddon from '../../../../../shared/hooks/global/UseAddon';
import AllocationControl from '../../../../../shared/components/allocation-control/AllocationControl';
import { AGENCY_HUB } from '../../../../../shared/routes/path';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { useProjectWizard } from '../../contexts/ProjectWizardContext';
import { ProjectData } from '../../models/ProjectData';
import {
  brightDataCountriesWithoutGlobal,
  isAdminOrOwner,
  isContainText,
  normalizeKeyword,
} from '../../../../../shared/utils/utils';
import Loader from '../../../../../shared/components/common/loader/Loader';
import AnalyzeLoader, {
  ANALYZE_LOADER_TOTAL_MS,
  BAR_FINISH_MS,
} from '../analyze-loader/AnalyzeLoader';
import ProjectSetupLoader from '../project-setup-loader/ProjectSetupLoader';
import useCanManage from '../../../../../shared/hooks/account/UseCanManage';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../../../shared/components/dropdown-item/DropdownItem';
import { useGetSearchVolume } from '../../../../../shared/api/hooks/project/useGetSearchVolume';
import { useProjectSubmit } from '../../hooks/UseProjectSubmit';
import { useModuleAccess } from '../../../../../shared/hooks/global/UseModuleAccess';
import MonitoringScheduleSelect from '../../../../../shared/components/common/monitoring-schedule-select/MonitoringScheduleSelect';
import { MONITORING_SCHEDULE_PRESETS } from '../../../../../shared/models/projects/MonitoringInterval';

interface CustomQuestion {
  text: string;
  checked: boolean;
  disabled: boolean;
  hasBrandConflict: boolean;
}

const SecondStep: React.FC = () => {
  const [apiError, setApiError] = useState<string | null>(null);
  const { accountResponse } = useAccount();
  const {
    activeStep,
    questionResponse,
    setProjectData,
    setSearchVolume,
    setActiveStep,
    domain,
    setDomain,
    setFirmName,
    monitoringPreset,
    setMonitoringPreset,
  } = useProjectWizard();
  const { canManage } = useCanManage();
  // No project yet, so the account's own addons decide; a full super admin passes without the addon.
  const scheduleAccess = useModuleAccess('MANAGE_MONITORING_SCHEDULE', null, { allowSuperAdmin: true });
  const { t } = useTranslation();

  const [brand, setBrand] = useState<string | null>(null);
  const [projectCountry, setProjectCountry] = useState<string | null>(null);
  const [countryError, setCountryError] = useState(false);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchVolumeCompleted, setSearchVolumeCompleted] = useState<boolean>(false);
  // Agency Hub accounts share one pool of prompts between projects. Only OWNER / ADMIN may reserve a
  // part of it for the new project (the allocation control); anyone else creates it with no limit, as
  // does an admin who leaves the field empty. Either way the selected prompts must fit what the pool
  // still has (`freePool`) — the counter above the list says by how much they do not.
  // The floor is the selected prompts and follows the selection UP only — unticking a prompt leaves
  // the reservation alone, since a reserve is the point (decision 2026-10-23).
  const hasAgencyHub = useAddon('AGENCY_HUB');
  const canAllocate = hasAgencyHub && !!accountResponse && isAdminOrOwner(accountResponse.role);
  const freePool = accountResponse
    ? Math.max(0, accountResponse.maxMonitorCount - accountResponse.allocatedMonitorCount)
    : 0;
  // What the pool already holds: with the hub that includes other projects' reservations, so the
  // pre-selection, "select all" and the limits header start from the same free pool as `overLimit`.
  // Ticking prompts by hand stays bounded by the plan (`canAddQuestion`), which is what lets the
  // counter show "N over allocation limit" instead of silently refusing the tick.
  const poolUsed = hasAgencyHub
    ? accountResponse?.allocatedMonitorCount || 0
    : accountResponse?.createdMonitorCount || 0;
  const [promptAllocation, setPromptAllocation] = useState<number | null>(null);
  const { submitProject, createdProjectId, loaderCompleted, setupProgress, leaveSetup } =
    useProjectSubmit();
  const { fetchSearchVolume, isLoading } = useGetSearchVolume();

  const canShowSearchVolumeStep = Boolean(
    accountResponse && accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin
  );
  const withSchedule = (data: ProjectData): ProjectData =>
    scheduleAccess.canAccess ? { ...data, monitoringInterval: MONITORING_SCHEDULE_PRESETS[monitoringPreset] } : data;

  useEffect(() => {
    if (questionResponse) {
      const seen = new Set<string>();
      const maxMonitorCount = accountResponse?.maxMonitorCount || 0;

      let maxSelected = maxMonitorCount - poolUsed;
      const predefinedQuestions = questionResponse.questions
        .filter((question) => {
          const normalized = question.trim().toLowerCase();
          if (seen.has(normalized)) {
            return false;
          }
          seen.add(normalized);
          return true;
        })
        .map((question) => {
          const questionData = {
            text: question,
            checked: maxSelected > 0,
            disabled: maxSelected <= 0,
            hasBrandConflict: false,
          };
          maxSelected = maxSelected - 1;
          return questionData;
        });

      setCustomQuestions(predefinedQuestions);
      setBrand(questionResponse.brand || null);
      setProjectCountry(questionResponse.projectCountry || null);
    }
  }, [questionResponse]);

  const back = () => {
    const step = domain && domain.trim() !== '' ? 'DOMAIN' : 'FIRM_NAME';
    setDomain(null);
    setFirmName(null);
    setActiveStep(step);
  };

  const checkedCustomQuestions = useMemo(
    () => customQuestions.filter((q) => q.checked).map((q) => q.text),
    [customQuestions]
  );

  // Master selector: how many can still be selected, and whether we're already at that max.
  const selectableLimit = Math.max(0, (accountResponse?.maxMonitorCount || 0) - poolUsed);
  const maxSelectable = Math.min(customQuestions.length, selectableLimit);
  const allSelected = maxSelectable > 0 && checkedCustomQuestions.length >= maxSelectable;

  const toggleSelectAll = () => {
    setApiError(null);
    setCustomQuestions((prev) => {
      if (allSelected) {
        return prev.map((q) => ({ ...q, checked: false }));
      }
      // Select from the top up to the limit; the effect on canAddQuestion normalizes `disabled`.
      let remaining = selectableLimit;
      return prev.map((q) => {
        const checked = remaining > 0;
        if (checked) {
          remaining -= 1;
        }
        return { ...q, checked };
      });
    });
  };

  const allCheckedQuestions = useMemo(() => [...checkedCustomQuestions], [checkedCustomQuestions]);
  const minAllocation = allCheckedQuestions.length;
  // null = no limit (the field is empty).
  const allocation = promptAllocation === null ? null : Math.max(promptAllocation, minAllocation);
  // Not enough pool for even the selected prompts (or the account is over-allocated): the step blocks.
  // An admin is pointed at the hub, where other projects' allocations can be lowered.
  const overLimit = hasAgencyHub && accountResponse ? Math.max(0, minAllocation - freePool) : 0;
  const poolShort = overLimit > 0;

  const canAddQuestion = useMemo(() => {
    if (!accountResponse) {
      return false;
    }
    const { maxMonitorCount, createdMonitorCount } = accountResponse;
    const checkedCount = allCheckedQuestions.length;
    const limit = maxMonitorCount - createdMonitorCount;

    return canManage && checkedCount < limit;
  }, [accountResponse, allCheckedQuestions]);

  useEffect(() => {
    setCustomQuestions((prevCustomQuestions) =>
      prevCustomQuestions.map((q) => ({
        ...q,
        disabled: !q.checked && !canAddQuestion,
      }))
    );
  }, [canAddQuestion]);

  const onCustomQuestionChange = (index: number, checked: boolean) => {
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index].checked = checked;
      updated[index].disabled = !checked && !canAddQuestion;

      return updated;
    });
  };

  const onCustomQuestionTextChange = (index: number, input: HTMLInputElement) => {
    const value = input.value;
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        text: value,
        hasBrandConflict: isContainText(brand, value) || false,
      };
      return updated;
    });
  };

  const createProjectData = (): ProjectData => {
    const uniqueQuestions: string[] = [];
    const seen = new Set<string>();

    for (const rawQuestion of allCheckedQuestions) {
      const question = rawQuestion.trim();
      if (question !== '' && !seen.has(question)) {
        seen.add(question);
        uniqueQuestions.push(question);
      }
    }

    return {
      ids: questionResponse?.ids || [],
      firmName: brand ? normalizeKeyword(brand) : brand,
      domain,
      questions: uniqueQuestions,
      projectCountry,
      ...(canAllocate && allocation !== null
        ? { promptAllocation: Math.max(allocation, uniqueQuestions.length) }
        : {}),
    } as ProjectData;
  };

  const addCustomQuestion = () => {
    setApiError(null);
    if (canAddQuestion) {
      setCustomQuestions((prev) => [
        ...prev,
        { text: '', checked: true, disabled: false, hasBrandConflict: false },
      ]);
    }
  };

  const allQuestionsCount = () => {
    const all = poolUsed;
    const selected = customQuestions.filter((q) => q.checked === true).length;
    return all + selected;
  };

  const renderLimits = () => {
    const countQuestionsWithSelected = allQuestionsCount();
    const max = accountResponse?.maxMonitorCount || 0;
    if (countQuestionsWithSelected > max) {
      return (
        <>
          <span>{Intl.NumberFormat('pl-PL').format(countQuestionsWithSelected)}</span> /{' '}
          {Intl.NumberFormat('pl-PL').format(max)}
        </>
      );
    }

    return (
      <>
        {Intl.NumberFormat('pl-PL').format(countQuestionsWithSelected)} /{' '}
        {Intl.NumberFormat('pl-PL').format(max)}
      </>
    );
  };

  if (brand === null) {
    return <Loader />;
  }

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (activeStep !== 'QUESTIONS') {
      return;
    }

    // Rynek musi być wskazany — projekt bez kraju zbierałby dane globalnie, a tej opcji świadomie
    // już nie ma na liście. Walidujemy przy próbie przejścia dalej, a nie blokadą przycisku, żeby
    // użytkownik zobaczył POWÓD, zamiast klikać w martwy guzik.
    if (!projectCountry) {
      setCountryError(true);
      return;
    }

    if (allCheckedQuestions.length === 0) {
      setApiError(t('selectMinimumOne'));
      return;
    }
    if (poolShort) {
      return;
    }

    setApiError(null);
    setSearchVolumeCompleted(false);
    setIsSubmitting(true);

    try {
      const projectData = createProjectData();
      setProjectData(projectData);

      if (canShowSearchVolumeStep) {
        const minDisplay = new Promise((resolve) => setTimeout(resolve, ANALYZE_LOADER_TOTAL_MS));
        const [searchVolumeResponses] = await Promise.all([
          fetchSearchVolume({
            ids: projectData.ids,
            questions: projectData.questions,
            firmName: projectData.firmName,
            projectCountry: projectData.projectCountry,
          }),
          minDisplay,
        ]);
        // Real work done → fill the bar to 100%, hold briefly so it visibly completes, then advance.
        setSearchVolumeCompleted(true);
        await new Promise((resolve) => setTimeout(resolve, BAR_FINISH_MS));
        setSearchVolume(searchVolumeResponses);
        setActiveStep('SEARCH_VOLUME');
      } else {
        await submitProject(withSchedule(projectData));
      }
    } catch (err: any) {
      setApiError(err.response?.data?.error || t('unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="project-wizard__page-box project-wizard__page-box--question">
      {isSubmitting &&
        (canShowSearchVolumeStep ? (
          <AnalyzeLoader completed={searchVolumeCompleted} />
        ) : (
          <ProjectSetupLoader
            progress={setupProgress}
            projectId={createdProjectId}
            completed={loaderCompleted}
            onNavigate={leaveSetup}
          />
        ))}

      <div className="project-wizard__form">
        <div className="form-box__row">
          <h2>{t('addCustomQuestions')}:</h2>
          <div className="project-wizard__limits">
            {t('currentLimits')}: {renderLimits()}
          </div>
          {domain && domain.trim() !== '' && (
            <div className="form-box__brand">
              <div className="form__label">{t('foundBrandName')}:</div>
              <InputText
                className="input--xxl"
                defaultValue={brand || ''}
                onChange={(e) => setBrand(e.target.value)}
              />
            </div>
          )}
          <div className="form-box__country">
            <div className="form__label">
              {t('country')}:
              <CustomTooltip tooltipText="countryMarketHint" />
            </div>
            <Dropdown
              value={
                brightDataCountriesWithoutGlobal.find((country) => country.code === projectCountry) ||
                null
              }
              onChange={(e) => {
                setProjectCountry(e.value.code);
                setCountryError(false);
                setApiError(null);
              }}
              placeholder={t('selectCountry')}
              options={brightDataCountriesWithoutGlobal}
              className={`dropdown dropdown--xxl ${countryError ? 'form-box__invalid' : ''}`}
              optionLabel="name"
              itemTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              // Odkąd kraj może być NIEwybrany, PrimeReact woła ten szablon z `null`, żeby
              // wyrenderować placeholder — bez tej gałęzi komponent wywala się na `option.icon`.
              valueTemplate={(option, props) =>
                option ? (
                  <DropdownItem icon={option.icon} name={option.name} />
                ) : (
                  <DropdownItem name={props.placeholder ?? ''} />
                )
              }
              filter
            />
            {countryError && <span>{t('countryRequired')}</span>}
          </div>
          {canAllocate && (
            <div className="form-box__allocation">
              <div className="form__label">
                {t('promptAllocation')}:
                <CustomTooltip tooltipText="promptAllocationHint" />
              </div>
              <AllocationControl
                value={allocation}
                min={minAllocation}
                max={Math.max(minAllocation, freePool)}
                free={Math.max(0, freePool - (allocation ?? minAllocation))}
                onChange={setPromptAllocation}
                disabled={poolShort}
              />
              {poolShort && (
                <span className="form-box__allocation-short">
                  {t('promptAllocationMissing', { count: overLimit })}{' '}
                  <NavLink to={AGENCY_HUB}>{t('agencyHub')}</NavLink>
                </span>
              )}
            </div>
          )}
          {scheduleAccess.canAccess && (
            <MonitoringScheduleSelect value={monitoringPreset} onChange={setMonitoringPreset} variant="wizard" />
          )}
          {customQuestions.length > 0 && (
            <div className="select-all-row">
              <div className={`select-all-row__count${overLimit > 0 ? ' select-all-row__count--over' : ''}`}>
                {t('selectedCount', { selected: checkedCustomQuestions.length })}
                {overLimit > 0 && <> {t('overAllocationLimit', { count: overLimit })}</>}
              </div>
              <button
                type="button"
                className="select-all-row__action"
                onClick={toggleSelectAll}
                disabled={maxSelectable === 0}
              >
                {t(allSelected ? 'deselectAll' : 'selectAll')}
              </button>
            </div>
          )}
          <ul>
            {customQuestions.map((customQuestion, index) => (
              <li key={`custom-question-${index}`}>
                <div className="list-block">
                  <Checkbox
                    id={`checkbox-question-${index}`}
                    inputId={`custom-question-${index}`}
                    onChange={(e) => onCustomQuestionChange(index, e.checked || false)}
                    checked={customQuestion.checked}
                    disabled={customQuestion.disabled}
                  />
                  <InputText
                    placeholder={`${t('customQuestion')} ${index + 1}`}
                    value={customQuestion.text}
                    onChange={(e) => onCustomQuestionTextChange(index, e.target)}
                  />
                  {customQuestion.disabled && (
                    <Tooltip
                      target={`#checkbox-question-${index}`}
                      content={t('limitRestriction')}
                      className="tooltip--top"
                      position="top"
                      style={{ width: '220px' }}
                      showDelay={5}
                    />
                  )}
                </div>
                {customQuestion.hasBrandConflict && (
                  <>
                    <span className="material-symbols-outlined" id={`warming-icon-${index}`}>
                      warning
                    </span>
                    <Tooltip
                      target={`#warming-icon-${index}`}
                      content={t('brandNameRestriction')}
                      className="tooltip--top"
                      position="top"
                      style={{ width: '220px' }}
                      showDelay={5}
                    />
                  </>
                )}
              </li>
            ))}
            <li className="add-new">
              <div className="list-block">
                <label />
                <Button
                  id="add-new-btn"
                  onClick={addCustomQuestion}
                  type="button"
                  className={`btn btn--link btn--link-black ${
                    canAddQuestion ? '' : 'btn--disabled-tooltip'
                  }`}
                >
                  {t('addCustomQuestions')}
                </Button>
                {!canAddQuestion && (
                  <Tooltip
                    target={`#add-new-btn`}
                    content={t('limitRestriction')}
                    className="tooltip--top"
                    position="top"
                    style={{ width: '220px' }}
                    showDelay={5}
                  />
                )}
              </div>
            </li>
          </ul>

          {apiError && <span className="error-message">{apiError}</span>}
        </div>
      </div>

      <div className="project-wizard__action project-wizard__action--inline">
        <Button onClick={back} className="btn btn--secondary btn--xxl" disabled={isSubmitting}>
          {t('back')}
        </Button>
        <Button
          onClick={handleSubmit}
          type="submit"
          className="btn btn--xxl"
          disabled={isSubmitting || brand.trim() === '' || allCheckedQuestions.length === 0 || poolShort}
        >
          {t(canShowSearchVolumeStep ? 'next' : 'saveAndLaunch')}
        </Button>
      </div>
    </div>
  );
};

export default SecondStep;
